import { useRef, useEffect } from 'react'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  className?: string
}

const RichTextEditor = ({ value, onChange, placeholder, rows = 10, className = '' }: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value
    }
  }, [value])

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }
  }

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    handleInput()
  }

  return (
    <div className={`border-2 border-gray-600 rounded-lg bg-gray-700 ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-2 border-b border-gray-600 bg-gray-800 rounded-t-lg">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-bold"
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm italic"
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm underline"
          title="Underline"
        >
          U
        </button>
        <div className="w-px h-6 bg-gray-600 mx-1"></div>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', 'h2')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          title="Heading"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', 'p')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          title="Paragraph"
        >
          P
        </button>
        <div className="w-px h-6 bg-gray-600 mx-1"></div>
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          title="Bullet List"
        >
          •
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          title="Numbered List"
        >
          1.
        </button>
        <div className="w-px h-6 bg-gray-600 mx-1"></div>
        <button
          type="button"
          onClick={() => execCommand('justifyLeft')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          title="Align Left"
        >
          ⬅
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyCenter')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          title="Align Center"
        >
          ⬌
        </button>
        <button
          type="button"
          onClick={() => execCommand('justifyRight')}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          title="Align Right"
        >
          ➡
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[200px] px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-light rounded-b-lg overflow-y-auto"
        style={{ minHeight: `${rows * 1.5}rem` }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
      
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
    </div>
  )
}

export default RichTextEditor

